import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { mockOfficeDashboard, mockOfficeTrend, withDevMock } from '@/lib/mock/officeDashboard';

/**
 * 공무(매출·원가·수금)의 데이터 접근 — 백엔드 `menu/pmis/office`(`/api/v1/pmis/office/*`).
 *
 * <b>목 저장소도, localStorage 도 쓰지 않는다.</b> 초안 단계에는 브라우저에 값을 두었지만(2026-08-11),
 * 표가 서면서 정본은 서버다 — 마감·잠금·증빙 판정이 전부 서버에 있고, 대시보드가 같은 표를 읽는다.
 *
 * <p>조회는 현장 하나로 좁혀 나간다. 서버가 그 현장 하나로 소속 격리(계층2)를 판정하기 때문이다.
 */

// ── 타입 (서버 응답과 1:1) ───────────────────────────────────────────────────

export type OfficeSettingsRow = {
  startDate: string | null;
  endDate: string | null;
  targetCompletionDate: string | null;
  targetCostRate: number | null;
  /** 본사관리비 요율 % (V167). 비면 본사관리비 줄이 통째로 빈다 — 0% 가 아니다 */
  overheadRate: number | null;
  advanceRate: number | null;
  progressRate: number | null;
  retentionRate: number | null;
  claimCycleMonths: number | null;
  collectTerm: string | null;
  /**
   * 수금예정일의 기산점 (V268) — 수금관리 탭이 이 둘로 회차별 예정일을 낸다.
   *
   * <p>{@link collectTerm} 을 대신하지 않는다. 앞엣것은 계약서에 적힌 말 그대로이고 이것은
   * 날짜를 만드는 값이다. 둘 중 하나라도 비면 예정일을 만들지 않는다 — 짐작해 앉히면
   * 현금흐름 곡선이 실제와 무관한 모양이 된다.
   */
  collectBase: 'CLAIM_DATE' | 'CLAIM_MONTH_END' | 'INVOICE_DATE' | null;
  collectOffsetDays: number | null;
  openingPlanProgress: number | null;
  openingActualProgress: number | null;
  openingCost: number | null;
  openingInvoice: number | null;
  openingCollect: number | null;
  status: 'DRAFT' | 'CONFIRMED';
  confirmedAt: string | null;
  /**
   * 공사금액을 잠근 시각 — <b>설정 확정과 다른 잠금</b>(사용자 확정 2026-09-10).
   *
   * <p>현장 수정 모달의 공사금액 카드가 [저장]과 함께 건다. 설정 확정은 공사기간·목표원가율·
   * 수금비율이 모두 있어야 서지만, 계약금액은 그것들과 무관하게 <b>계약서에 이미 적혀 있는 값</b>
   * 이라 먼저 굳는다. 푸는 길은 설정 확정과 같다 — 설계변경(사유+증빙).
   */
  contractLockedAt: string | null;
  /** 표의 월 열 — 착공 몇 개월 전부터 · 준공 몇 개월 후까지 (V616). 재시작 전 서버는 안 보낸다 */
  monthPadBefore?: number | null;
  monthPadAfter?: number | null;
  updatedAt: string | null;
};

export type OfficeTradeRow = {
  id: string;
  trade: string;
  amountWithVat: number | null;
  amountWithoutVat: number | null;
  sortOrder: number;
};

export type OfficeMonthRow = {
  periodYm: string;
  /** 보할 공정표에서 들여온 값. 사람이 못 고친다. */
  planProgressRate: number | null;
  planSourceAt: string | null;
  actualProgressRate: number | null;
  /** LEDGER = 주별 원장(주간회의)을 따라간다 · MANUAL = 표에 직접 적었다 */
  actualSource: 'LEDGER' | 'MANUAL';
  costAmount: number | null;
  /** 본사관리비 — MANUAL 인 달에만 값이 있다. RATE 인 달은 표가 요율로 낸다(V167) */
  overheadAmount: number | null;
  /** RATE = 설정의 요율을 따라간다 · MANUAL = 그 달만 직접 적었다 */
  overheadSource: 'RATE' | 'MANUAL';
  invoiceAmount: number | null;
  collectAmount: number | null;
  /** LEDGER = 수금관리 회차(발행일이 그 달인 도급기성)를 따라간다 · MANUAL = 표에 직접 (V277) */
  invoiceSource: 'LEDGER' | 'MANUAL';
  /** LEDGER = 기성 원장(수금 완료 회차)을 따라간다 · MANUAL = 표에 직접 적었다 (V99) */
  collectSource: 'LEDGER' | 'MANUAL';
  note: string | null;
  status: 'OPEN' | 'CONFIRMED';
  confirmedAt: string | null;
  reopenReason: string | null;
  /** 낙관적 잠금의 기준. 저장할 때 그대로 돌려보낸다. */
  updatedAt: string | null;
  /** DETAIL = 투입금액은 내역의 합 · MANUAL = 표에서 합계를 직접 쳤다 (V580). 재시작 전 서버는 안 보낸다 */
  costSource?: 'DETAIL' | 'MANUAL';
};

/**
 * 투입금액 한 칸 — <b>공종 × 비목</b>(V180 · 요청 No.35).
 *
 * @property trade    빈 문자열이면 「공종 없음」(현장 운영경비처럼 어느 공종에도 안 붙는 돈)
 * @property costKind SUBCONTRACT 외주 · MATERIAL 자재 · EXPENSE 경비 · ETC 미분류.
 *                    서버가 아직 이 칸을 안 주는 배포에서도 화면이 서게 optional 이다
 */
export type OfficeCostDetailRow = {
  periodYm: string;
  trade: string;
  costKind?: string | null;
  amount: number | null;
  /** LEDGER = 서버가 원장 합을 넣은 칸 · MANUAL = 사람이 친 칸 (V580). 재시작 전 서버는 안 보낸다 */
  source?: 'LEDGER' | 'MANUAL';
};

/**
 * 비목 승인 한 줄 — 그 달 그 비목이 <b>확정</b>이다(V507 · DOC-125). 줄이 없으면 미확정.
 * 경비는 인사총무팀, 외주·자재는 외주구매팀이 승인한다. 미분류는 승인이 없다.
 */
export type OfficeCostApprovalRow = {
  periodYm: string;
  costKind: string;
  approvedBy: string | null;
  approvedByName?: string | null;
  approvedAt: string;
};

/**
 * 그 달 원장에 <b>실제로 잡힌</b> 돈 (V180).
 *
 * <p>「이만큼 잡힙니다 [불러오기]」가 읽는 값이다. <b>정본이 아니라 제안</b>이다 — 잡자재·경비는
 * 현장을 이름으로 이어서 현장명이 바뀌면 안 잡히고, 직불처럼 원장을 안 거친 돈도 있다.
 */
export type OfficeCostLedgerRow = {
  trade: string;
  costKind: string;
  amount: number | null;
  /** 몇 건이 모인 값인가. 「0원 한 건」과 「아예 없음」을 가른다 */
  entryCount: number;
};

/**
 * 진척 원장 한 주(DOC-38). 값은 <b>그 주까지의 누계</b>다(2026-08-13 확정).
 *
 * <p>주간회의가 준 값(`weeklyActualRate`)과 효력 값(`actualRate`)이 나란히 온다 — 둘이 다르면
 * 화면이 "회의에는 3.5%, 공무에는 4.0%" 라고 말할 수 있어야 한다.
 */
export type OfficeProgressWeekRow = {
  /** 아직 아무도 쓰지 않은 주는 표에 행이 없다 — 그때는 null 이고 <b>날짜가 열쇠</b>다 */
  weekId: string | null;
  weekLabel: string | null;
  startDate: string | null;
  endDate: string | null;
  periodYm: string;
  weeklyActualRate: number | null;
  actualRate: number | null;
  /** WEEKLY = 주간회의를 따라간다 · OFFICE = 공무에서 직접 넣었다 */
  source: 'WEEKLY' | 'OFFICE';
};

/**
 * 공종별 도급기성 한 덩어리 — <b>진행률의 근거</b>다(DOC-38 §5).
 *
 * <p>"기성 100억 중 건축 20억·토목 10억" 이 채워진 만큼이 그 공종이 나간 정도다. 표에는 그리지 않고
 * 실적공정률 창에서만 본다(사용자 확정 2026-08-12).
 */
export type OfficeClaimSum = {
  periodYm: string | null;
  /** 옛 기성에는 없다 — 화면이 '공종 미지정' 으로 세운다 */
  trade: string | null;
  amount: number | null;
};

export type OfficeInvoiceRow = {
  id: string;
  /**
   * 어디서 온 증빙인가 (2026-09-15). `PAYMENT` 는 <b>수금관리 회차에 붙인 파일</b>이다 —
   * 같은 서류를 두 번 올리지 않도록 공무 표가 그대로 센다. 지우는 자리는 그 회차 창이다.
   */
  source?: 'OFFICE' | 'PAYMENT';
  /** `PAYMENT` 일 때 몇 회차인가(「3회차」·「선급금」) */
  origin?: string | null;
  periodYm: string;
  invoiceNo: string | null;
  issueDate: string | null;
  amount: number | null;
  fileName: string;
  verified: boolean;
  downloadUrl: string | null;
};

export type OfficeChangeOrderRow = {
  id: string;
  seq: number;
  reason: string;
  fileName: string | null;
  beforeWithoutVat: number | null;
  beforeWithVat: number | null;
  effectiveYm: string | null;
  openedAt: string | null;
  downloadUrl: string | null;
};

/** 화면이 표 하나를 그리는 데 필요한 전부. */
export type OfficeSheetResponse = {
  siteId: string;
  settings: OfficeSettingsRow | null;
  trades: OfficeTradeRow[];
  months: OfficeMonthRow[];
  costDetails: OfficeCostDetailRow[];
  /** 주별 실적. 표의 월 칸은 사람이 직접 치고, 비워 두면 이 줄들에서 채워진다 */
  progressWeeks: OfficeProgressWeekRow[];
  /** 공종별 도급기성(진행률의 근거). 표가 아니라 창에서만 쓴다 */
  claims: OfficeClaimSum[];
  invoices: OfficeInvoiceRow[];
  changeOrders: OfficeChangeOrderRow[];
  /** 마감 기한(익월 N일). 정책값이라 화면이 박아 두지 않는다. */
  closingDay: number;
  /** 투입금액 비목별 승인(V507). 재시작 전 서버는 안 주므로 optional 이다 */
  costApprovals?: OfficeCostApprovalRow[];
  /** 메뉴 수정권한 없이도 외주·자재·경비를 넣는 사람(외주구매팀·인사총무팀). 입력 칸을 열지만 정한다 */
  costKindEditor?: boolean;
};

/**
 * 설정 저장 본문.
 *
 * <p>`overheadRate` 를 뺀다(V167) — 본사가 넣는 칸이라 저장 경로와 권한이 따로다
 * (`saveOverheadRate`). 여기 실어 보내면 현장이 보낸 빈 값이 본사가 넣어 둔 요율을 지운다.
 */
export type OfficeSettingsPayload = Omit<
  OfficeSettingsRow,
  'status' | 'confirmedAt' | 'updatedAt' | 'overheadRate' | 'contractLockedAt'
>;

export type OfficeTradePayload = {
  id: string | null;
  trade: string;
  amountWithVat: number | null;
  amountWithoutVat: number | null;
};

export type OfficeMonthPayload = {
  periodYm: string;
  actualProgressRate: number | null;
  /** 이번 저장에서 실적공정률 칸을 건드렸는가. 켜야만 서버가 그 칸을 본다 */
  actualProgressEdited: boolean;
  invoiceAmount: number | null;
  collectAmount: number | null;
  /** 이번 저장에서 세금계산서 발행 칸을 건드렸는가. 켜야만 서버가 그 칸을 본다 (V277) */
  invoiceEdited: boolean;
  /** 이번 저장에서 실수금 칸을 건드렸는가. 켜야만 서버가 그 칸을 본다 — 실적공정률과 같은 규약 */
  collectEdited: boolean;
  /** 본사관리비. 적으면 그 달만 요율에서 풀리고, 비워 보내면 다시 요율을 따라간다(V167) */
  overheadAmount: number | null;
  /** 이번 저장에서 본사관리비 칸을 건드렸는가 */
  overheadEdited: boolean;
  /** 표에서 친 투입금액 합계. 적으면 그 달은 내역의 합 대신 이 값, 비워 보내면 다시 내역의 합 (V580) */
  costAmount?: number | null;
  /** 이번 저장에서 투입금액 합계 칸을 건드렸는가 */
  costEdited?: boolean;
  note: string | null;
  expectedUpdatedAt: string | null;
};

export type OfficePlanCurveResult = { applied: number; skipped: string[] };

/**
 * 공정표에서 설정을 지어 준 결과(사용자 확정 2026-08-13).
 *
 * <p>`filled` 는 채운 것, `missing` 은 <b>공정표가 모르는 값</b>이라 사람이 넣어야 하는 칸이다.
 * `scale` 은 금액에 곱한 배수 — 서버가 계약금액과 견주어 짚은 값이고, 화면이 한 번에 바꿀 수 있다.
 */
export type OfficeAutofillResult = {
  applied: boolean;
  reason: string | null;
  filled: string[];
  missing: string[];
  scale: number;
  sheet: OfficeSheetResponse | null;
};

/**
 * 공정표에서 지어 본 공종별 금액(DOC-38 §7).
 *
 * <p>`amount` 는 <b>공정표에 적힌 수 그대로</b>다 — 원인지 천원인지는 파일마다 달라, 곱하는 것은 화면이
 * 사람에게 물어 정한다.
 */
export type OfficeTradeSuggestion = { trade: string; amount: number | null };

/**
 * 들여오기 미리보기 한 줄 — "어디에 무슨 값이 들어가는지".
 *
 * @property skipReason 못 넣는 달의 이유(마감됨·공사기간 밖). 넣을 수 있으면 null
 */
export type OfficePlanCurvePreviewRow = {
  periodYm: string;
  rate: number | null;
  cumulative: number | null;
  skipReason: string | null;
};

export function usePlanCurvePreview(siteId: string) {
  return useQuery({
    queryKey: ['office', 'plan-curve-preview', siteId] as const,
    queryFn: () =>
      apiFetch<OfficePlanCurvePreviewRow[]>(`/v1/pmis/office/sites/${siteId}/plan-curve/preview`),
    enabled: !!siteId,
    retry: false,
  });
}

export function useTradeSuggestions(siteId: string, depth: number, enabled: boolean) {
  return useQuery({
    queryKey: ['office', 'trade-suggestions', siteId, depth] as const,
    queryFn: () =>
      apiFetch<OfficeTradeSuggestion[]>(
        `/v1/pmis/office/sites/${siteId}/settings/trade-suggestions?depth=${depth}`,
      ),
    enabled: enabled && !!siteId,
    retry: false,
  });
}

// ── 캐시 키 ──────────────────────────────────────────────────────────────────

export const officeKeys = {
  sheet: (siteId: string) => ['office', 'sheet', siteId] as const,
  dashboard: (mode: string, year: number, month: number | null, status: string) =>
    ['office', 'dashboard', mode, year, month ?? 0, status] as const,
  trend: (year: number) => ['office', 'trend', year] as const,
  rowNotes: (siteId: string) => ['office', 'row-notes', siteId] as const,
};

// ── 줄별 특이사항 (V505) ─────────────────────────────────────────────────────

export type OfficeRowNote = { rowKey: string; note: string };

export function useOfficeRowNotes(siteId: string) {
  return useQuery({
    queryKey: officeKeys.rowNotes(siteId),
    queryFn: () => apiFetch<OfficeRowNote[]>(`/v1/pmis/office/sites/${siteId}/row-notes`),
    enabled: !!siteId,
    retry: false,
  });
}

/** 빈 글은 그 줄의 특이사항을 지운다. 서버가 그 현장의 전부를 돌려준다. */
export function useSaveOfficeRowNote(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OfficeRowNote) =>
      apiFetch<OfficeRowNote[]>(`/v1/pmis/office/sites/${siteId}/row-notes`, { method: 'PUT', body: payload }),
    onSuccess: (notes) => queryClient.setQueryData(officeKeys.rowNotes(siteId), notes),
  });
}

// ── 현장 시트 ────────────────────────────────────────────────────────────────

export function useOfficeSheet(siteId: string) {
  return useQuery({
    queryKey: officeKeys.sheet(siteId),
    queryFn: () => apiFetch<OfficeSheetResponse>(`/v1/pmis/office/sites/${siteId}`),
    enabled: !!siteId,
    // 기성 탭도 공종 목록을 얻으려고 이 질의를 탄다 — 공무 권한이 없는 사람에게는 403 이고,
    // 그때는 조용히 물러서야 한다(세 번 더 두드릴 이유가 없다).
    retry: false,
  });
}

/**
 * 내보내기 주소 — 서버가 <b>설정과 산식으로 시트를 다시 지어</b> 내려준다(DOC-37 §9).
 *
 * <p>브라우저가 만들지 않는 이유가 둘이다. 화면은 한 해씩만 그리는데 파일은 전 기간이 나와야 하고,
 * 권한 판정과 서식이 서버 한 곳에 있어야 한다. `apiFetch` 대신 링크(`<a href>`)로 여는 것은
 * 세션 쿠키가 그대로 실리고 브라우저가 파일 이름을 서버에서 받기 때문이다.
 */
export const officeExportUrl = {
  excel: (siteId: string) => `/api/v1/pmis/office/sites/${siteId}/export.xlsx`,
  pdf: (siteId: string) => `/api/v1/pmis/office/sites/${siteId}/export.pdf`,
};

/**
 * 쓰기 경로 전부.
 *
 * <p>서버가 <b>매번 시트 전체를 돌려준다</b> — 한 칸을 고치면 합계·잠금·증빙 상태가 함께 움직이므로,
 * 부분 응답을 받아 화면에서 기워 붙이면 언젠가 어긋난다. 응답을 그대로 캐시에 앉힌다.
 */
export function useOfficeMutations(siteId: string) {
  const queryClient = useQueryClient();
  const put = (sheet: OfficeSheetResponse) => queryClient.setQueryData(officeKeys.sheet(siteId), sheet);
  const base = `/v1/pmis/office/sites/${siteId}`;

  return {
    saveSettings: useMutation({
      mutationFn: (payload: OfficeSettingsPayload) =>
        apiFetch<OfficeSheetResponse>(`${base}/settings`, { method: 'PUT', body: payload }),
      onSuccess: put,
    }),
    saveTrades: useMutation({
      mutationFn: (trades: OfficeTradePayload[]) =>
        apiFetch<OfficeSheetResponse>(`${base}/settings/trades`, { method: 'PUT', body: { trades } }),
      onSuccess: put,
    }),
    /**
     * 본사관리비 요율 — <b>확정된 뒤에도</b> 들어간다(V167 · 요청 #35 「본사확인 후 확정」).
     *
     * <p>설정 저장과 경로가 다른 이유는 문이 다르기 때문이다. 이 값은 도급계약이 아니라 회사가
     * 정하는 값이라 설계변경의 대상이 아니고, 서버가 확정 권한을 요구한다.
     */
    saveOverheadRate: useMutation({
      mutationFn: (overheadRate: number | null) =>
        apiFetch<OfficeSheetResponse>(`${base}/settings/overhead-rate`, {
          method: 'PUT',
          body: { overheadRate },
        }),
      onSuccess: put,
    }),
    /** 표의 월 범위(착공 전 · 준공 후 개월 수, 0~12) — 확정 뒤에도 들어간다(V616). */
    saveMonthPad: useMutation({
      mutationFn: (pad: { monthPadBefore: number; monthPadAfter: number }) =>
        apiFetch<OfficeSheetResponse>(`${base}/settings/month-pad`, { method: 'PUT', body: pad }),
      onSuccess: put,
    }),
    confirmSettings: useMutation({
      mutationFn: () => apiFetch<OfficeSheetResponse>(`${base}/settings/confirm`, { method: 'POST' }),
      onSuccess: put,
    }),
    /** 설계변경 — 확정된 설정을 여는 유일한 길. 증빙을 먼저 올리고 키를 싣는다. */
    openByChangeOrder: useMutation({
      mutationFn: async ({ reason, file }: { reason: string; file: File }) => {
        const ticket = await uploadOfficeFile(siteId, file);
        return apiFetch<OfficeSheetResponse>(`${base}/change-orders`, {
          method: 'POST',
          body: { reason, fileName: file.name, objectKey: ticket.key, effectiveYm: null },
        });
      },
      onSuccess: put,
    }),
    /** 최고관리자 해제 — 증빙·사유 없이 바로 연다(V404). 다른 사람이면 서버가 403 으로 답한다. */
    unlockSettings: useMutation({
      mutationFn: () => apiFetch<OfficeSheetResponse>(`${base}/settings/unlock`, { method: 'POST' }),
      onSuccess: put,
    }),

    /** 월 임시저장(배치). 붙여넣기처럼 여러 달이 한꺼번에 바뀌는 일이 잦아 배열로 보낸다. */
    saveMonths: useMutation({
      mutationFn: (months: OfficeMonthPayload[]) =>
        apiFetch<OfficeSheetResponse>(`${base}/months`, { method: 'PUT', body: { months } }),
      onSuccess: put,
    }),
    /**
     * 공정표에서 설정을 통째로 채운다 — 화면이 탭에 들어올 때 한 번 부른다.
     *
     * <p>이미 있는 값은 덮지 않고 확정된 설정은 건드리지 않으므로 여러 번 불러도 같다.
     */
    autofill: useMutation({
      // 금액 단위는 현장의 공정표 금액 단위를 따른다(V565) — 고치는 자리는 공정관리 탭이다.
      mutationFn: () => apiFetch<OfficeAutofillResult>(`${base}/settings/autofill`, { method: 'POST' }),
      onSuccess: (result) => {
        if (result.sheet) put(result.sheet);
      },
    }),
    /** 실적공정률을 주별로 나눠 넣는다(DOC-38). 표의 월 칸은 사람이 직접 치는 자리다. */
    saveProgressWeeks: useMutation({
      mutationFn: (weeks: { weekStartDate: string; actualRate: number | null; manual: boolean }[]) =>
        apiFetch<OfficeSheetResponse>(`${base}/progress-weeks`, { method: 'PUT', body: { weeks } }),
      onSuccess: put,
    }),
    saveCostDetails: useMutation({
      mutationFn: ({
        periodYm,
        details,
        expectedUpdatedAt,
      }: {
        periodYm: string;
        /** 「공종 × 비목」 한 칸씩(V180). `trade` 가 빈 문자열이면 「공종 없음」이다. */
        details: { trade: string; costKind: string; amount: number | null }[];
        expectedUpdatedAt: string | null;
      }) =>
        apiFetch<OfficeSheetResponse>(`${base}/months/${periodYm}/cost-details`, {
          method: 'PUT',
          body: { details, expectedUpdatedAt },
        }),
      onSuccess: put,
    }),
    /** 비목 승인(DOC-125). 손이 아니면 서버가 403 사유로 답한다. */
    approveCostKind: useMutation({
      mutationFn: ({
        periodYm,
        costKind,
        expectedUpdatedAt,
      }: {
        periodYm: string;
        costKind: string;
        expectedUpdatedAt: string | null;
      }) =>
        apiFetch<OfficeSheetResponse>(`${base}/months/${periodYm}/cost-approvals/${costKind}`, {
          method: 'POST',
          body: { expectedUpdatedAt },
        }),
      onSuccess: put,
    }),
    unapproveCostKind: useMutation({
      mutationFn: ({ periodYm, costKind }: { periodYm: string; costKind: string }) =>
        apiFetch<OfficeSheetResponse>(`${base}/months/${periodYm}/cost-approvals/${costKind}`, {
          method: 'DELETE',
        }),
      onSuccess: put,
    }),
    importPlanCurve: useMutation({
      mutationFn: (points: { periodYm: string; cumulativeRate: number }[] | null) =>
        apiFetch<OfficePlanCurveResult>(`${base}/plan-curve/import`, {
          method: 'POST',
          body: { points: points ?? [] },
        }),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: officeKeys.sheet(siteId) }),
    }),

    confirmMonth: useMutation({
      mutationFn: ({ periodYm, note }: { periodYm: string; note: string | null }) =>
        apiFetch<OfficeSheetResponse>(`${base}/months/${periodYm}/confirm`, { method: 'POST', body: { note } }),
      onSuccess: put,
    }),
    reopenMonth: useMutation({
      mutationFn: ({ periodYm, reason }: { periodYm: string; reason: string }) =>
        apiFetch<OfficeSheetResponse>(`${base}/months/${periodYm}/reopen`, { method: 'POST', body: { reason } }),
      onSuccess: put,
    }),

    /** 세금계산서 — 파일을 저장소에 직접 올리고 서버에는 키만 보낸다. */
    addInvoice: useMutation({
      mutationFn: async ({ periodYm, file, amount }: { periodYm: string; file: File; amount: number }) => {
        const ticket = await uploadOfficeFile(siteId, file);
        return apiFetch<OfficeSheetResponse>(`${base}/months/${periodYm}/invoices`, {
          method: 'POST',
          body: { invoiceNo: null, issueDate: null, amount, fileName: file.name, objectKey: ticket.key },
        });
      },
      onSuccess: put,
    }),
    deleteInvoice: useMutation({
      mutationFn: (invoiceId: string) =>
        apiFetch<OfficeSheetResponse>(`/v1/pmis/office/invoices/${invoiceId}`, { method: 'DELETE' }),
      onSuccess: put,
    }),
  };
}

/**
 * presigned PUT 업로드. ① 서버에서 자리를 받고 ② 저장소로 파일을 직접 PUT 한다.
 * 파일 바이트가 API 를 지나지 않으므로 큰 PDF 가 백엔드 스레드를 물지 않는다.
 *
 * <p><b>현장을 함께 보낸다.</b> 증빙이 앉는 자리가 그 현장의 폴더이기 때문이다
 * (`docs/site/{현장}/공무증빙/` · 2026-08-21). 예전에는 파일명만 보냈고, 서버는 어느 현장인지
 * 모르는 채 `hr/{uuid}/` 에 부었다 — 저장소를 열어도 무슨 증빙인지 알 수 없었다.
 */
async function uploadOfficeFile(siteId: string, file: File): Promise<{ key: string; url: string }> {
  const ticket = await apiFetch<{ key: string; url: string }>('/v1/pmis/office/uploads', {
    method: 'POST',
    body: { siteId, fileName: file.name },
  });
  const response = await fetch(ticket.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!response.ok) {
    throw new Error(`파일을 저장소에 올리지 못했습니다. (HTTP ${response.status})`);
  }
  return ticket;
}

// ── 투입금액 줄별 첨부 (V509) ────────────────────────────────────────────────

/** 한 줄은 투입금액 칸과 같은 축이다 — 항목별은 ('', 비목), 공종별은 (공종, ETC). */
export type OfficeCostFile = {
  id: string;
  trade: string;
  costKind: string;
  fileName: string;
  url: string | null;
  createdAt: string;
};

const costFilesKey = (siteId: string, ym: string) => ['office', 'cost-files', siteId, ym] as const;

export function useOfficeCostFiles(siteId: string, ym: string) {
  return useQuery({
    queryKey: costFilesKey(siteId, ym),
    queryFn: () => apiFetch<OfficeCostFile[]>(`/v1/pmis/office/sites/${siteId}/months/${ym}/cost-files`),
    enabled: !!siteId && !!ym,
    retry: false,
  });
}

export function useOfficeCostFileMutations(siteId: string, ym: string) {
  const queryClient = useQueryClient();
  const put = (files: OfficeCostFile[]) => queryClient.setQueryData(costFilesKey(siteId, ym), files);
  return {
    add: useMutation({
      mutationFn: async ({ trade, costKind, file }: { trade: string; costKind: string; file: File }) => {
        const ticket = await uploadOfficeFile(siteId, file);
        return apiFetch<OfficeCostFile[]>(`/v1/pmis/office/sites/${siteId}/months/${ym}/cost-files`, {
          method: 'POST',
          body: { trade, costKind, fileName: file.name, objectKey: ticket.key },
        });
      },
      onSuccess: put,
    }),
    remove: useMutation({
      mutationFn: (fileId: string) =>
        apiFetch<OfficeCostFile[]>(`/v1/pmis/office/cost-files/${fileId}`, { method: 'DELETE' }),
      onSuccess: put,
    }),
  };
}

// ── 전사 대시보드 ────────────────────────────────────────────────────────────

/**
 * 집계는 <b>서버가</b> 한다(DOC-37 §10.3). 브라우저가 더하면 현장 시트와 대시보드가 언젠가 다른
 * 숫자를 말한다. 비율까지 담겨 오므로 화면은 나눗셈을 하지 않는다.
 */
export type OfficeDashboardRow = {
  siteId: string;
  siteName: string;
  siteStatus: string;
  siteCategory: string;
  ready: boolean;
  hasData: boolean;
  contractExVat: number | null;
  contractIncVat: number | null;
  targetCostRate: number | null;
  periodRevenue: number | null;
  periodCost: number | null;
  periodCollect: number | null;
  cumPlanProgress: number | null;
  cumProgress: number | null;
  cumRevenue: number | null;
  cumCost: number | null;
  cumCollect: number | null;
  /** 화면에서는 감춰 두었지만 값은 계속 온다(DOC-37 §14 ②). */
  receivable: number | null;
  costRate: number | null;
  collectRate: number | null;
  lastConfirmedYm: string | null;
  overdueCount: number;
};

export type OfficeDashboardTotals = {
  siteCount: number;
  readyCount: number;
  overdueSites: number;
  contractExVat: number | null;
  periodRevenue: number | null;
  periodCost: number | null;
  periodProfit: number | null;
  periodCollect: number | null;
  cumRevenue: number | null;
  cumCost: number | null;
  receivable: number | null;
  costRate: number | null;
  profitRate: number | null;
};

export type OfficeDashboardResponse = {
  mode: string;
  year: number;
  month: number | null;
  rows: OfficeDashboardRow[];
  totals: OfficeDashboardTotals;
};

export type OfficeTrendPoint = {
  periodYm: string;
  revenue: number | null;
  cost: number | null;
  collect: number | null;
};

/** 화면이 건 조건 그대로. 조회와 내보내기가 같은 문자열을 써야 파일과 화면이 어긋나지 않는다. */
function dashboardQuery(mode: 'month' | 'year', year: number, month: number, status: string): string {
  const query = new URLSearchParams({ mode, year: String(year) });
  if (mode === 'month') query.set('month', String(month));
  if (status !== 'all') query.set('status', status);
  return query.toString();
}

/**
 * 대시보드 내보내기 주소 — <b>보고 있던 기간·상태 그대로</b> 나간다.
 *
 * <p>4월을 보다가 눌렀는데 12월치가 나오면 그 파일은 두 번 다시 믿기지 않는다.
 */
export const officeDashboardExportUrl = {
  excel: (mode: 'month' | 'year', year: number, month: number, status: string) =>
    `/api/v1/office/dashboard/export.xlsx?${dashboardQuery(mode, year, month, status)}`,
  pdf: (mode: 'month' | 'year', year: number, month: number, status: string) =>
    `/api/v1/office/dashboard/export.pdf?${dashboardQuery(mode, year, month, status)}`,
};

/**
 * 전 현장 매출·원가·수금.
 *
 * <p>개발계에서 <b>줄이 하나도 없으면</b> 목으로 채운다({@code mock/officeDashboard.ts}). 개발계
 * DB 에는 현장이 적어 넣는 공무 시트가 없어 이 표가 늘 비어 있었고, 그러면 표를 고쳐도 고친 것이
 * 안 보인다. 운영에서는 목이 한 번도 불리지 않는다.
 */
export function useOfficeDashboard(mode: 'month' | 'year', year: number, month: number, status: string) {
  const query = dashboardQuery(mode, year, month, status);

  return useQuery({
    queryKey: officeKeys.dashboard(mode, year, mode === 'month' ? month : null, status),
    queryFn: () =>
      withDevMock(
        '매출·원가·수금',
        () => apiFetch<OfficeDashboardResponse>(`/v1/office/dashboard?${query}`),
        (data) => data.rows.length === 0,
        () => mockOfficeDashboard(mode, year, month, status),
      ),
  });
}

export function useOfficeTrend(year: number, enabled: boolean) {
  return useQuery({
    queryKey: officeKeys.trend(year),
    queryFn: () =>
      withDevMock(
        '월별 추이',
        () => apiFetch<OfficeTrendPoint[]>(`/v1/office/dashboard/trend?year=${year}`),
        (points) => points.length === 0,
        () => mockOfficeTrend(year),
      ),
    enabled,
  });
}
